<script setup>
import { ref, onMounted, computed, nextTick, onUnmounted, watch } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { isIphone } from "@/utils/functions";

const storeRequests = useRequestsStore();

const activeBtn = ref("weekly");
const email = ref("");
const searchError = ref(false);
const isSearch = ref(false);
const scrollContainer = ref(null);
const sentinel = ref(null);
let observer = null;

const overflowAuto = computed(() => (storeRequests.winnersList?.length >= 5 ? "--overflow-auto" : ""));
const customScrollBar = computed(() => (storeRequests.winnersList?.length >= 5 && isIphone ? "list-scrollbar" : ""));

const winnersList = Array.from({ length: 10 }, (_, index) => {
	return {
		id: index + 1,
		name: "Александр Ф.",
		email: "user@ya.ru",
		prizeName: index === 3 ? "Сертификат Яндекс Афиша" : "Prize name",
	};
});


const filterList = [
	{
		type: "weekly",
		prize: "Eженедельный",
	},
	{
		type: "monthly",
		prize: "Eжемесячный",
	},
];

const activeFilterBtn = async (type) => {
	if(!!email.value) {
		await storeRequests.getEmailWinnings(email.value, type);
		activeBtn.value = type;
	} else {
		activeBtn.value = type;
		storeRequests.updateWinnings();
		await storeRequests.getWinnings(type);
		setupObserver();
	}
};

// watch(() => email.value, () => {
// 	if(!!email.value) {
// 		searchError.value = false;
// 		isSearch.value = false
// 	}
// })

const checkEmail = async () => {
	const emailPattern = /^(?![._%+\-])([A-Za-z0-9._%+\-]{1,})@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
	const isValid = emailPattern.test(email.value) && email.value.length >= 5;
	if(!!email.value && isValid) {
		await storeRequests.getEmailWinnings(email.value, activeBtn.value);
		isSearch.value = true;
        searchError.value = false;		
	} else {
		searchError.value = true;
        isSearch.value = false;
	}
}

const clearEmail = async () => {
    email.value = "";
	searchError.value = false;

	await activeFilterBtn(activeBtn.value);

	isSearch.value = false;
}

const setupObserver = async () => {
	await nextTick();
	
	if (!sentinel.value || !scrollContainer.value) return;

	if (observer) {
        observer.disconnect();
    }
	
	observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];
			
			if (entry.isIntersecting && !storeRequests.loadingPrize && !storeRequests.allLoadedPrize) {
				storeRequests.getWinnings(activeBtn.value);
			}
		},
		{
			root: scrollContainer.value,
			rootMargin: "0px 0px 10px 0px",
			threshold: 0,
		},
	);

	observer.observe(sentinel.value);
};
onMounted(async () => {
	await storeRequests.getWinnings(activeBtn.value);
	setupObserver();
});
onUnmounted(() => {
	if (observer && sentinel.value) {
		observer.unobserve(sentinel.value);
		observer.disconnect();
	}
});
</script>
<template>
	<section :class="['winners section']" id="winners">
		<div class="container">
			<div class="winners__block">
				<h2 class="title2 winners__title">Победители</h2>
				<div class="winners-filter">
					<button 
						v-for="filter in filterList" 
						:class="['winners-filter__btn btn --small --border-grey', { '--blue': activeBtn === filter.type }]" 
						@click="activeFilterBtn(filter.type)"
					>
						{{ filter.prize }}
					</button>
					<label class="winners-filter__label">
						<button class="winners-filter__search-btn"  v-show="!isSearch" @click="checkEmail">
							<svg width="24" height="24"><use href="#search"></use></svg>
						</button>
						<button class="winners-filter__search-btn --clear"  v-show="isSearch" @click="clearEmail">
							<svg width="24" height="24"><use href="#x"></use></svg>
						</button>
						<input 
							@input="searchError = false"
							@keyup.enter="checkEmail"
							type="text" 
							:class="['winners-filter__search', { '--error': searchError }]" 
							:placeholder="searchError ? 'Введите email' : 'Поиск по email'" 
							v-model="email"
						/>
					</label>
				</div>
				<Transition>
					<ul :class="['winners__list', overflowAuto, customScrollBar]" v-if="storeRequests.winnersList?.length" ref="scrollContainer">
						<li class="winners__item" v-for="{ winner, prize, id} in storeRequests.winnersList" :key="id">
							<span>{{ winner.name }}</span>
							<span>{{ winner.email }}</span>
							<span>{{ prize.title }}</span>
						</li>
						<!-- Элемент, за которым мы наблюдаем -->
						<li ref="sentinel" class="sentinel" style="position: relative;height: 30px;" v-if="!storeRequests.allLoadedPrize">
							<img class="form__loader" style="position: absolute;left: 50%; transform: translateX(-50%);" width="36" height="36" src="@/assets/img/icons/loader_black.svg" alt="изображение" />
						</li>
					</ul>
					<div class="winners__empty" v-else>
						<p v-if="!storeRequests.loadingPrize">
							<!-- <span>Пользователь с такой почтой не найден</span> -->
							<span>Победители ещё не выбраны</span>
						</p>
						<div class="winners__loading" v-else>
							<img class="form__loader" style="position: absolute; left: 50%; transform: translateX(-50%)" width="36" height="36" src="@/assets/img/icons/loader_black.svg" alt="изображение" />
						</div>
					</div>
				</Transition>
			</div>
		</div>
	</section>
</template>
<style lang="scss" scoped>
@use "@/assets/scss/base/vars";
@use "@/assets/scss/base/base";
.winners {
	position: relative;
}

.winners-filter {
	width: 100%;
	display: grid;
	grid-template-columns: auto auto 1fr;
	gap: 1.6rem;
	margin-bottom: 6.4rem;
	@media (max-width: vars.$m) {
		grid-template-columns: 1fr 1fr;
	}
	@media (max-width: vars.$x480) {
		margin-bottom: 3rem;
		gap: 1rem;
	}
}

.winners-filter__btn {
	&.--blue {
		color: #fff;
		pointer-events: none;
		@media (hover: hover){
			&:hover {
				color: var(--text-accent-1-100);
				background: var(--text-inverse-100);
			}
		}
	}
}
.winners-filter__label {
	position: relative;
	width: 100%;
	@media (max-width: vars.$m) {
		grid-column: 1/-1;
	}
}
.winners-filter__search-btn {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	right: 2.4rem;
	svg {
		fill: #0C4CA3;
		stroke: transparent;
	}
	&.--clear {
		svg {
			fill: transparent;
			stroke: #0C4CA3;
		}
	}
}
.winners-filter__search {
	width: 100%;
	border: 1px solid var(--border-inverse-3);
	outline: none;
	border-radius: 6.4rem;
	padding: 1.6rem 5rem 1.6rem 2.4rem;
	font-weight: 500;
	font-size: 2.4rem;
	line-height: 100%;
	color: inherit;
	&::placeholder {
		color: var(--text-default-40);
	}
	@media (max-width: vars.$x480) {
		font-size: 1.6rem;
		padding: 1.4rem 2.2rem;
	}
	&.--error {
		border-color: var(--status-error-1);
		box-shadow: 0 0 0 4px rgba(243, 101, 101, 0.24);
	}
}
.winners__list {
	
	&.--overflow-auto {
		max-height: 51.6rem;
		padding-right: 2rem;
		margin-right: -2.5rem;
		@media (max-width: vars.$m) {
			margin-right: 0rem;
		}
		// @extend .list-scrollbar;
	}
}
.winners__item {
	display: grid;
	grid-template-columns: 1fr 44rem 27.4rem;
	align-items: center;
	gap: 0.5rem;

	border: 1px solid rgba(14, 14, 14, 0.16);
	border-radius: var(--border-radius);
	padding: 4rem 2.4rem;

	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	@media (max-width: vars.$t) {
		grid-template-columns: repeat(3, 1fr);
	}
	@media (max-width: vars.$xx) {
		grid-template-columns: repeat(2, 1fr);
	}
	@media (max-width: vars.$x480) {
		grid-template-columns: 1fr;
		font-size: 1.6rem;
		gap: 6px;
        padding: 1.7rem 1.6rem 1.5rem;
	}
	&:not(:last-child) {
		margin-bottom: 2rem;
	}
	span {
		&:nth-child(3) {
			@media (max-width: vars.$xx) {
				grid-column: 1/-1;
			}
		}
	}
}
.winners__empty {
	position: relative;
	font-weight: 400;
	font-size: 2.4rem;
	line-height: 140%;
	text-align: center;
	@media (max-width: vars.$x480) {
		font-size: 2rem;
	}
}
.winners__loading {
}

.v-enter-active,
.v-leave-active {
	transition: all 0.2s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
	// transform: translateY(10px);
}
</style>
